import { localDate, type StartingBalances } from "@teamna/shared";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  Button,
  CellField,
  DataTable,
  ErrorState,
  InlineError,
  Notice,
  SegmentedControl,
  Select,
  SkeletonList,
  StatTile,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useSaveStartingBalances, useStartingBalances } from "@teamna/web-client/hooks/use-setup.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { formatDate } from "@teamna/web-client/lib/format.ts";
import { describeWeekdays, SaveBar, SetupFrame, Stepper } from "./frame.tsx";
import { phoneText } from "../families/model.ts";

/**
 * The last setup step (mobile spec 4.4 and 5.3): the sessions each person has left when the
 * business moves to Teamna.
 *
 * A family business ("Sessions left this month"): one table per family. Families join in the middle
 * of a period. The default for each participant is the number of their usual days left until the
 * period ends; the owner changes it with the stepper, picks the package it belongs to, and marks
 * the family paid or owed.
 *
 * Where clients book for themselves ("Sessions left and end date", feature O1): one table with a row
 * for each client. A client starts with no package. The owner picks the package, the sessions left,
 * the last day of the package and paid or owed. The package then ends on that day.
 *
 * "Save and notify" writes the starting balances and tells each person their number, which marks
 * the step done. Both shapes use the same columns, with the end date and paid or owed in the row
 * where clients book for themselves.
 */
export function SetupBalancesScreen() {
  return <SetupFrame step="balances">{(businessId) => <BalancesBody businessId={businessId} />}</SetupFrame>;
}

type Family = StartingBalances["families"][number];
/** One person's row, with the family (or the client) it belongs to. */
type Row = Family["participants"][number] & { family: Family };
type Entry = { sessions: number; packageId: string | null; endsOn: string | null };

/** A family row is the participant; a client row is the client, who may have no profile here yet. */
function rowKey(row: Row, self: boolean): string {
  return self ? row.family.userId : (row.id ?? row.family.userId);
}

function balanceColumns({
  label,
  entries,
  setEntries,
  packages,
  keyOf,
  self,
}: {
  label: ReturnType<typeof useLabels>["label"];
  entries: Record<string, Entry>;
  setEntries: Dispatch<SetStateAction<Record<string, Entry>>>;
  packages: StartingBalances["packages"];
  keyOf: (row: Row) => string;
  /** Where clients book for themselves: the end date and paid or owed in each row. */
  self: { today: string; periodEnd: string; paid: Record<string, boolean>; setPaid: Dispatch<SetStateAction<Record<string, boolean>>> } | null;
}): DataTableColumn<Row>[] {
  const entryOf = (row: Row): Entry =>
    entries[keyOf(row)] ?? { sessions: row.currentSessions ?? row.defaultSessions, packageId: row.packageId, endsOn: row.endsOn ?? null };
  const update = (row: Row, patch: Partial<Entry>) =>
    setEntries((current) => ({ ...current, [keyOf(row)]: { ...entryOf(row), ...patch } }));

  const name: DataTableColumn<Row> = {
    id: "name",
    header: label(self ? "member" : "participant", { capital: true }),
    cell: (row) => (
      <div>
        <p className="font-semibold text-ink" dir="auto">
          {row.displayName}
        </p>
        {self ? (
          <p className="text-[13px] text-ink-2 tabular-nums">
            {row.family.phoneE164 ? phoneText(row.family.phoneE164) : row.family.phoneMasked}
          </p>
        ) : row.groupName ? (
          <p className="text-[13px] text-ink-3">{row.groupName}</p>
        ) : null}
      </div>
    ),
  };
  const days: DataTableColumn<Row> = {
    id: "days",
    header: "Usual days",
    cell: (row) => describeWeekdays(row.usualWeekdays),
    hideOnPhone: true,
  };
  const pkg: DataTableColumn<Row> = {
    id: "package",
    header: "Package",
    width: "220px",
    cell: (row) => (
      <Select
        label={`Package for ${row.displayName}`}
        className="w-full"
        value={entryOf(row).packageId ?? ""}
        onChange={(event) => update(row, { packageId: event.target.value || null })}
      >
        <option value="">No package</option>
        {packages.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Select>
    ),
  };
  const sessions: DataTableColumn<Row> = {
    id: "sessions",
    header: "Sessions left",
    width: "150px",
    cell: (row) => (
      <Stepper
        label={`Sessions left for ${row.displayName}`}
        value={entryOf(row).sessions}
        min={0}
        max={60}
        onChange={(next) => update(row, { sessions: next })}
      />
    ),
  };
  if (!self) {
    const fallback: DataTableColumn<Row> = {
      id: "default",
      header: "Default",
      hideOnPhone: true,
      align: "end",
      cell: (row) => <span className="text-ink-3 tabular-nums">{row.defaultSessions}</span>,
    };
    return [name, days, pkg, sessions, fallback];
  }
  const endsOn: DataTableColumn<Row> = {
    id: "endsOn",
    header: "Ends on",
    width: "170px",
    cell: (row) => {
      const entry = entryOf(row);
      const value = entry.endsOn ?? self.periodEnd;
      return (
        <CellField
          label={`End date for ${row.displayName}`}
          type="date"
          min={self.today}
          value={value}
          invalid={value < self.today}
          disabled={!entry.packageId}
          onChange={(event) => update(row, { endsOn: event.target.value || null })}
        />
      );
    },
  };
  const paid: DataTableColumn<Row> = {
    id: "paid",
    header: "Paid or owed",
    width: "170px",
    cell: (row) => (
      <PaidToggle
        name={row.displayName}
        paid={self.paid[row.family.userId] ?? row.family.paid}
        onChange={(next) => self.setPaid((current) => ({ ...current, [row.family.userId]: next }))}
      />
    ),
  };
  return [name, pkg, sessions, endsOn, paid];
}

function BalancesBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const { forChildren } = useShape();
  const { business } = useActiveBusiness();
  const data = useStartingBalances(businessId);
  const save = useSaveStartingBalances(businessId);
  const self = !forChildren;
  const keyOf = (row: Row) => rowKey(row, self);
  // What the API proposes; the owner's changes sit on top until the next save.
  const initial = useMemo(() => {
    const entries: Record<string, Entry> = {};
    const paid: Record<string, boolean> = {};
    for (const family of data.data?.families ?? []) {
      paid[family.userId] = family.paid;
      for (const participant of family.participants) {
        entries[rowKey({ ...participant, family }, self)] = {
          sessions: participant.currentSessions ?? participant.defaultSessions,
          // A client starts with no package: only the ones the owner gives a package are saved.
          packageId: participant.packageId ?? (self ? null : (data.data?.packages[0]?.id ?? null)),
          endsOn: participant.endsOn ?? null,
        };
      }
    }
    return { entries, paid };
  }, [data.data, self]);
  const [entryEdits, setEntries] = useState<Record<string, Entry>>({});
  const [paidEdits, setPaid] = useState<Record<string, boolean>>({});
  const entries = useMemo(() => ({ ...initial.entries, ...entryEdits }), [initial, entryEdits]);
  const paid = useMemo(() => ({ ...initial.paid, ...paidEdits }), [initial, paidEdits]);
  const today = localDate(new Date(), business?.timezone);

  const rows: Row[] = useMemo(
    () => (data.data?.families ?? []).flatMap((family) => family.participants.map((participant) => ({ ...participant, family }))),
    [data.data],
  );

  const totals = useMemo(() => {
    // Where clients book for themselves, only a client with a package counts.
    const families = (data.data?.families ?? []).filter((family) => !self || Boolean(entries[family.userId]?.packageId));
    const sessions = rows
      .filter((row) => !self || Boolean(entries[rowKey(row, self)]?.packageId))
      .reduce((sum, row) => sum + (entries[rowKey(row, self)]?.sessions ?? 0), 0);
    const familiesPaid = families.filter((family) => paid[family.userId] !== false).length;
    return { sessions, familiesPaid, familiesOwing: families.length - familiesPaid };
  }, [entries, paid, data.data, rows, self]);

  if (data.isLoading) return <SkeletonList rows={4} />;
  if (data.error || !data.data) {
    return <ErrorState error={data.error ?? new Error("missing")} onRetry={() => void data.refetch()} />;
  }

  const { families, packages, periodEnd, renewsOn } = data.data;
  const familyWord = label("member");
  const chosen = rows.filter((row) => entries[keyOf(row)]?.packageId);
  const lateEnd = self && chosen.some((row) => (entries[keyOf(row)]?.endsOn ?? periodEnd) < today);

  function submit() {
    const list = chosen.map((row) => {
      const entry = entries[keyOf(row)]!;
      return self
        ? {
            memberUserId: row.family.userId,
            packageId: entry.packageId!,
            sessions: entry.sessions,
            paid: paid[row.family.userId] !== false,
            endsOn: entry.endsOn ?? periodEnd,
          }
        : {
            participantId: row.id!,
            packageId: entry.packageId!,
            sessions: entry.sessions,
            paid: paid[row.family.userId] !== false,
          };
    });
    if (list.length === 0 || lateEnd) return;
    save.mutate(
      { entries: list },
      {
        onSuccess: () => {
          setEntries({});
          setPaid({});
          showToast(`Saved. Each ${familyWord} can see their number now.`);
        },
      },
    );
  }

  const columns = balanceColumns({
    label,
    entries,
    setEntries,
    packages,
    keyOf,
    self: self ? { today, periodEnd, paid, setPaid } : null,
  });

  return (
    <div className="flex flex-col gap-6">
      {self ? (
        <p className="text-[15px] text-ink-2">
          For each {label("member")} with a package, choose the package, the sessions left and the last day of the package.
          Leave a {label("member")} with no package if they do not have one. Nobody pays before they see their number.
        </p>
      ) : (
        <p className="text-[15px] text-ink-2">
          The number for each {label("participant")} is how many of their usual days are left until{" "}
          {formatDate(`${periodEnd}T12:00:00Z`)}. Everyone renews on {formatDate(`${renewsOn}T12:00:00Z`)}.
          Change any number, then save. Nobody pays before they see their number.
        </p>
      )}

      <div className="grid grid-cols-3 gap-3 sm:max-w-[620px]">
        <StatTile label="Sessions left in total" value={totals.sessions} />
        <StatTile label="Paid" value={totals.familiesPaid} tone="good" />
        <StatTile
          label="Owed"
          value={totals.familiesOwing}
          tone={totals.familiesOwing > 0 ? "bad" : "neutral"}
        />
      </div>

      {packages.length === 0 ? (
        <Notice tone="warning" title="Set up a package first, so each starting number belongs to one." />
      ) : null}

      {families.length === 0 ? (
        <Notice tone="info" title={`No ${label(self ? "member" : "participant", { plural: true })} yet`}>
          {self
            ? `This fills in as you add ${label("member", { plural: true })} or they join.`
            : `This fills in as ${label("member", { plural: true })} join and add ${label("participant", { plural: true })}.`}
        </Notice>
      ) : self ? (
        <DataTable
          caption={`${label("member", { plural: true, capital: true })}: sessions left and end date`}
          columns={columns}
          rows={rows}
          rowKey={keyOf}
          empty={{ title: `No ${label("member", { plural: true })}`, message: "Nothing to set." }}
          countLabel={(count) => `${count} ${count === 1 ? label("member") : label("member", { plural: true })}`}
        />
      ) : (
        families.map((family) => (
          <section key={family.userId} className="flex flex-col gap-3" aria-label={family.memberName}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-[17px]">{family.memberName}</h2>
                <p className="text-[13px] text-ink-2 tabular-nums">{family.phoneE164 ? phoneText(family.phoneE164) : family.phoneMasked}</p>
              </div>
              <PaidToggle
                name={family.memberName}
                paid={paid[family.userId] !== false}
                onChange={(next) => setPaid((current) => ({ ...current, [family.userId]: next }))}
              />
            </div>
            <DataTable
              caption={`${family.memberName}: sessions left`}
              columns={columns}
              rows={rows.filter((row) => row.family.userId === family.userId)}
              rowKey={keyOf}
              empty={{ title: `No ${label("participant", { plural: true })}`, message: "Nothing to set." }}
              countLabel={(count) =>
                `${count} ${count === 1 ? label("participant") : label("participant", { plural: true })}`
              }
            />
          </section>
        ))
      )}

      {lateEnd ? <Notice tone="warning" title="An end date is before today. Choose today or a later day." /> : null}

      <SaveBar note={data.data.savedAt ? `Last saved ${formatDate(data.data.savedAt)}.` : undefined}>
        <Button
          loading={save.isPending}
          disabled={families.length === 0 || packages.length === 0 || (self && chosen.length === 0) || lateEnd}
          onClick={submit}
        >
          Save and notify
        </Button>
      </SaveBar>
      {save.error ? <InlineError error={save.error} /> : null}
    </div>
  );
}

/** Paid or owed for one family or one client: the shared `SegmentedControl`, in the status tones. */
function PaidToggle({
  name,
  paid,
  onChange,
}: {
  name: string;
  paid: boolean;
  onChange: (paid: boolean) => void;
}) {
  return (
    <SegmentedControl
      label={`Paid or owed: ${name}`}
      name={`paid-${name}`}
      size="compact"
      value={paid}
      onChange={onChange}
      options={[
        { value: true, label: "Paid", tone: "confirmed" },
        { value: false, label: "Owed", tone: "late" },
      ]}
    />
  );
}
